import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * QRScanModal — opens a camera viewfinder and reads room QR codes.
 *
 * The scanner first tries the environment-facing (back) camera which is what
 * tablets / phones need to scan room stickers. If only the user-facing (front)
 * camera is available — common on laptops — it falls back automatically.
 * A toggle button lets the user switch between cameras at any time.
 *
 * On successful scan we parse the URL/path and:
 *   - `/r/<code>`           → onResolved({ kind: "short", value: <code> })
 *   - `/room/<pin>` or pure
 *     6-digit numeric PIN   → onResolved({ kind: "pin", value: <pin> })
 *
 * Props:
 *   open, onOpenChange       — Radix Dialog controls
 *   onResolved({kind, value}) — parent handles navigation
 */
export default function QRScanModal({ open, onOpenChange, onResolved }) {
    const scannerRef = useRef(null);
    const containerId = "qr-scan-viewfinder";
    const [facing, setFacing] = useState("environment"); // "environment" | "user"
    const [error, setError] = useState(null);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setError(null);

        const start = async () => {
            try {
                const scanner = new Html5Qrcode(containerId, /* verbose */ false);
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: facing },
                    {
                        fps: 12,
                        // Square viewfinder ~70% of the smaller dimension
                        qrbox: (vw, vh) => {
                            const m = Math.min(vw, vh);
                            const s = Math.floor(m * 0.7);
                            return { width: s, height: s };
                        },
                        aspectRatio: 1.0,
                    },
                    (decoded) => {
                        if (cancelled) return;
                        handleDecoded(decoded);
                    },
                    () => { /* per-frame error — ignored */ }
                );
                if (!cancelled) setRunning(true);
            } catch (e) {
                if (cancelled) return;
                setError(
                    e?.message?.includes("Permission")
                        ? "Camera permission was denied. Please allow camera access and try again."
                        : "Couldn't start the camera. If you're on a laptop without a back camera, try the switch button."
                );
            }
        };

        start();
        return () => {
            cancelled = true;
            const s = scannerRef.current;
            scannerRef.current = null;
            if (s) safeStop(s).then(() => { try { s.clear(); } catch { /* noop */ } });
            setRunning(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, facing]);

    const handleDecoded = (text) => {
        if (!text) return;
        const parsed = parseRoomTarget(text);
        if (!parsed) {
            toast.error("This QR doesn't look like a TekWissen room code.");
            return;
        }
        // Stop the scanner so the camera light turns off before we navigate.
        const s = scannerRef.current;
        scannerRef.current = null;
        const done = () => {
            onResolved && onResolved(parsed);
            onOpenChange(false);
        };
        if (s) safeStop(s).then(done); else done();
    };

    const switchCamera = () => setFacing((f) => (f === "environment" ? "user" : "environment"));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="qr-scan-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Camera size={20} weight="duotone" className="text-[#0055FF]" />
                        Scan Room QR
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3 pt-2">
                    <div className="text-xs text-gray-500">
                        Point your camera at the QR sticker on the room&apos;s tablet or signage.
                    </div>

                    <div
                        id={containerId}
                        className="rounded-lg overflow-hidden bg-black aspect-square w-full grid place-items-center"
                        data-testid="qr-scan-viewfinder"
                    >
                        {!running && !error && (
                            <div className="text-sm text-gray-300">Starting camera…</div>
                        )}
                    </div>

                    {error && (
                        <div className="rounded border border-red-200 bg-red-50 text-red-700 text-xs p-2.5" data-testid="qr-scan-error">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-2 pt-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={switchCamera}
                            data-testid="qr-scan-switch-cam"
                        >
                            <ArrowsClockwise size={14} className="mr-1" />
                            {facing === "environment" ? "Use front camera" : "Use back camera"}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                            data-testid="qr-scan-close"
                        >
                            <X size={14} className="mr-1" /> Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Safely stop an Html5Qrcode instance. The library throws a *synchronous*
 * Error ("Cannot stop, scanner is not running or paused.") when stop() is
 * called on a scanner that isn't in the SCANNING state — for example when
 * the user closes the modal before the camera finished starting, or when
 * cleanup runs twice in React StrictMode. `.catch()` on the returned promise
 * is not enough since the throw happens before the promise is created.
 * Always resolves; never throws.
 */
function safeStop(scanner) {
    try {
        const state = scanner.getState && scanner.getState();
        const SCANNING = Html5QrcodeScannerState && Html5QrcodeScannerState.SCANNING;
        const PAUSED = Html5QrcodeScannerState && Html5QrcodeScannerState.PAUSED;
        // Only call stop when scanner is actually in a stoppable state.
        if (state !== SCANNING && state !== PAUSED) return Promise.resolve();
        return scanner.stop().catch(() => {});
    } catch {
        return Promise.resolve();
    }
}

/**
 * Parse a decoded QR string into a usable target. Accepts:
 *   - Full URLs                e.g. https://anything/r/havg
 *   - Path-only short URLs     e.g. /r/havg
 *   - Full /room/<pin> URLs    e.g. https://…/room/482963
 *   - Bare 6-8 digit PIN       e.g. 482963
 */
function parseRoomTarget(raw) {
    if (!raw) return null;
    const text = String(raw).trim();
    // Bare numeric PIN
    if (/^\d{4,8}$/.test(text)) return { kind: "pin", value: text };
    // Try parsing as URL or as a path
    try {
        const u = text.includes("://")
            ? new URL(text)
            : new URL(text, window.location.origin);
        const path = u.pathname;
        let m = path.match(/^\/r\/([a-z0-9]+)\/?$/i);
        if (m) return { kind: "short", value: m[1].toLowerCase() };
        m = path.match(/^\/room\/(\d{4,8})\/?$/);
        if (m) return { kind: "pin", value: m[1] };
    } catch { /* not a URL */ }
    return null;
}
